import React from 'react';
import { Col, Container, Input, Label, FormGroup } from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { useBoolean } from 'usehooks-ts';

import AppButton from 'components/App/Button';
import AppInputValidation from 'components/App/InputValidation';
import Breadcrumb from 'components/App/Breadcrumb';
import Loading from 'components/App/Loading';
import Meta from 'components/App/Meta';
import PlacePublishStatusBadge from 'components/App/PlacePublishStatusBadge';
import ModalEditLocation from 'components/Modals/ModalEditLocation';
// import ModalRequestAction from 'components/Modals/ModalRequestAction';
import SectionReviews from 'components/DashboardPage/Home/SectionReviews';
import SelectValidation from 'components/App/SelectValidation';
import { apiErrorHandler, showSuccess } from 'utils';
import placeApi from 'api/placeApi';

// import images from 'assets/images';
import { ReactComponent as StarSVG } from 'assets/images/star_purple500.svg';
import { ReactComponent as LocationYellow } from 'assets/images/LocationYellow.svg';
import { ReactComponent as LocationBlue } from 'assets/images/LocationBlue.svg';
import { LANDMARK_LIST, LOGIN_TYPE, PLACE_TYPE, LOCATIONS, storageKey } from 'constants/index';
import INITIAL_VALUE from 'constants/initialValue';
import { editInformationLocateValidation } from 'utils/validate';

import { landmarksOptions } from 'components/AddLocation/StepBasic/config';
import styles from './Location.module.scss';

const PLACE_STATUS_OPTIONS = [
  { value: 'CONTACTING', label: 'Liên hệ' },
  { value: 'ASSIGNED', label: 'Ký hợp đồng' },
  { value: 'READY', label: 'Sẵn sàng' },
  { value: 'CHANGED_OWNER', label: 'Đổi chủ' },
];

const breadcrumbList = [
  {
    name: 'Chỗ nghỉ',
    isActive: false,
    link: LOCATIONS.LOCATION_LIST,
  },
  {
    name: 'Tổng quát',
    isActive: true,
    link: '',
  },
];

function Location(props) {
  const {
    isLoadingGetPlaceDetail,
    isLoadingUpdatePlace,
    isLoadingUpdatePlaceOtaCredentials,

    placeDetail,
    provinces,
    districts,
    wards,
    bookingReviewList,

    actionGetPlaceDetail,
    actionUpdatePlace,
    // actionDeleteLocation,
    actionUpdatePlaceOtaCredentials,
    actionGetProvinceList,
    actionGetDistrictList,
    actionGetWardList,
    actionGetBookingReviewList,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const placeId = queryParams.get('place');
  const isOpenEditModalFromQuery = queryParams.get('edit') === 'true';

  const isExtranet = React.useMemo(
    () => localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
    [],
  );
  const setLocationValuesRef = React.useRef(null);

  const {
    value: isOpenModalEditLocation,
    setTrue: onOpenModalEditLocation,
    setFalse: onCloseModalEditLocation,
    toggle: onToggleModalEditLocation,
  } = useBoolean(false);

  // const {
  //   value: isOpenModalDeleteLocation,
  //   setTrue: onOpenModalDeleteLocation,
  //   setFalse: onCloseModalDeleteLocation,
  //   toggle: onToggleModalDeleteLocation,
  // } = useBoolean(false);

  // const onDeleteLocation = React.useCallback(() => {
  //   onOpenModalDeleteLocation();
  // }, [onOpenModalDeleteLocation]);

  // const onConfirmDeleteLocation = React.useCallback(() => {
  //   actionDeleteLocation({
  //     locationId: placeId,
  //     callback: onCloseModalDeleteLocation,
  //   });
  //   history.push(`${LOCATIONS.LOCATION_LIST}`);
  // }, [actionDeleteLocation, history, placeId, onCloseModalDeleteLocation]);

  const onChangePublished = React.useCallback(async () => {
    try {
      await placeApi.updatePlaceStatus({
        placeId,
        isPublished: !placeDetail?.isPublished,
      });
      showSuccess('Cập nhật trạng thái thành công');
      actionGetPlaceDetail({ placeId });
    } catch (error) {
      apiErrorHandler(error);
    }
  }, [actionGetPlaceDetail, placeId, placeDetail?.isPublished]);

  const onChangeStatus = React.useCallback(async (selectedOption) => {
    const selectedStatus = selectedOption.value;
    try {
      await placeApi.updatePlaceStatus({
        placeId,
        status: selectedStatus,
      });
      showSuccess('Cập nhật trạng thái thành công');
      actionGetPlaceDetail({ placeId });
    } catch (error) {
      apiErrorHandler(error);
    }
  }, [actionGetPlaceDetail, placeId]);

  const validation = useFormik({
    initialValues: {
      name: '',
      placeType: '',
      starRating: 0,
      province: '',
      market: '',
      district: '',
      ward: '',
      address: '',
      coordinates: '',
      description: '',
      landmarks: [],
      otaHotelId: '',
      otaHotelChannelKey: '',
    },

    validationSchema: Yup.object({
      name: editInformationLocateValidation.name,
      placeType: editInformationLocateValidation.placeType,
      starRating: editInformationLocateValidation.starRating,
      province: editInformationLocateValidation.province,
      market: editInformationLocateValidation.market,
      district: editInformationLocateValidation.district,
      ward: editInformationLocateValidation.ward,
      address: editInformationLocateValidation.address,
      coordinates: editInformationLocateValidation.coordinates,
      description: editInformationLocateValidation.description,
      landmarks: editInformationLocateValidation.landmarks,
    }),

    onSubmit: (values) => {
      actionUpdatePlace({
        placeId,
        placeInfo: {
          ...values,
          landmarks: values.landmarks?.map((item) => item.value),
        },
        callback: onCloseModalEditLocation,
      });
    },
  });

  setLocationValuesRef.current = validation.setValues;

  const otaValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      otaHotelId: placeDetail.otaHotelId || '',
      otaHotelChannelKey: placeDetail.otaHotelChannelKey || '',
    },
    onSubmit: (values) => {
      actionUpdatePlaceOtaCredentials({
        placeId,
        data: values,
        callback: () => actionGetPlaceDetail({ placeId }),
      });
    },
  });

  React.useEffect(() => {
    if (placeId) {
      actionGetPlaceDetail({ placeId });
    }
  }, [actionGetPlaceDetail, placeId]);

  React.useEffect(() => {
    if (isOpenEditModalFromQuery) {
      onOpenModalEditLocation();
      const nextQueryParams = new URLSearchParams(location.search);
      nextQueryParams.delete('edit');
      const nextSearch = nextQueryParams.toString();

      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
      });
    }
  }, [
    history,
    isOpenEditModalFromQuery,
    location.pathname,
    location.search,
    onOpenModalEditLocation,
  ]);

  const renderLandmarks = React.useMemo(() => {
    if (placeDetail.landmarks?.length > 0) {
      const filterLandmarks = placeDetail.landmarks?.map((item) => {
        const isObject = typeof item === 'object' && item !== null;
        const itemId = isObject ? (item._id || item.id) : item;
        const itemName = isObject ? (item.name || item.nameEN || '') : '';
        const foundLandmark = LANDMARK_LIST.find((land) => land.value === itemId);
        return foundLandmark?.label || itemName || itemId;
      });

      return `Khu vực ${filterLandmarks.filter(Boolean).join(', ')}`;
    }

    return null;
  }, [placeDetail]);

  React.useEffect(() => {
    const optionLandmarksByProvince = landmarksOptions
      .find((landmark) => landmark.label === placeDetail.province);

    const landmarksSelected = placeDetail.landmarks?.map((item) => {
      const isObject = typeof item === 'object' && item !== null;
      const itemId = isObject ? (item._id || item.id) : item;
      const itemName = isObject ? (item.name || item.nameEN || '') : '';
      const optionLabel = optionLandmarksByProvince?.options
        ?.find((landmark) => landmark.value === itemId)?.label;

      return {
        value: itemId,
        label: optionLabel || itemName || itemId,
      };
    }) || [];

    setLocationValuesRef.current({
      name: placeDetail.name,
      placeType: placeDetail.placeType,
      starRating: placeDetail.starRating,
      province: placeDetail.province,
      market: placeDetail.market?._id
        || placeDetail.market?.id
        || placeDetail.marketId
        || (typeof placeDetail.market === 'string' ? placeDetail.market : ''),
      district: placeDetail.district,
      ward: placeDetail.ward,
      address: placeDetail.address,
      coordinates: placeDetail.location?.coordinates.join(', '),
      description: placeDetail.description,
      landmarks: landmarksSelected,
      otaHotelId: placeDetail.otaHotelId || '',
      otaHotelChannelKey: placeDetail.otaHotelChannelKey || '',
    });
  }, [placeDetail]);

  // Update Place
  React.useEffect(() => {
    if (isExtranet) return;

    actionGetProvinceList({});
  }, [actionGetProvinceList, isExtranet]);

  React.useEffect(() => {
    if (isExtranet) return;

    if (validation.values.province && provinces.length > 0) {
      actionGetDistrictList({
        provinces: provinces.find((p) => p.name === validation.values.province)
          ?.id,
      });
    }
  }, [
    actionGetDistrictList,
    isExtranet,
    provinces,
    validation.values.province,
  ]);

  React.useEffect(() => {
    if (isExtranet) return;

    if (
      validation.values.district
      && validation.values.province
      && districts.length > 0
      && provinces.length > 0
    ) {
      actionGetWardList({
        provinces: provinces.find((p) => p.name === validation.values.province)
          ?.id,
        districts: districts.find((p) => p.name === validation.values.district)
          ?.id,
      });
    }
  }, [
    validation.values.district,
    validation.values.province,
    provinces,
    districts,
    actionGetWardList,
    isExtranet,
  ]);

  React.useEffect(() => {
    if (placeId) {
      actionGetBookingReviewList({
        place: placeId,
        limit: 5,
        page: 1,
      });
    }
  }, [actionGetBookingReviewList, placeId]);

  return (
    <>
      <Loading loading={isLoadingGetPlaceDetail} loaderOnPage />
      <div className={`page-content ${styles.CustomPage}`}>
        <Meta name="Chi tiết chỗ nghỉ" />

        <Container fluid>
          <Breadcrumb pageTitle="Tổng quát" breadcrumbList={breadcrumbList} />

          <div className={styles.Wrapper}>
            <div className={styles.Page}>
              <div className="row">
                <Col md={7} className={styles.LeftWrapper}>
                  <div className="Information">
                    <div className="InformationContent">
                      <div className="InformationContentTitle">
                        <p className="InformationContentTitle__Name">
                          {placeDetail.name}
                        </p>

                        <div className="InformationContentTitle__Star">
                          {Array.from(Array(placeDetail.starRating).keys()).map(
                            (item) => (
                              <StarSVG key={item} />
                            ),
                          )}
                        </div>
                      </div>

                      <div className="InformationContentSubTitle">
                        <span className="Text">
                          {PLACE_TYPE[placeDetail.placeType]?.label}
                        </span>
                        {placeDetail.totalRooms > 0 && (
                          <span className="Text">{`Tổng số phòng: ${placeDetail.totalRooms}`}</span>
                        )}
                        <PlacePublishStatusBadge isPublished={placeDetail.isPublished} />
                      </div>

                      <div className="d-flex flex-column flex-sm-row flex-wrap align-items-start align-items-sm-center gap-3 gap-sm-4 mt-4 mb-4">
                        <FormGroup switch className="d-flex align-items-center gap-2 mb-0">
                          <Input
                            type="switch"
                            id="isPublished"
                            name="isPublished"
                            checked={!!placeDetail.isPublished}
                            onChange={onChangePublished}
                            disabled={placeDetail?.status === 'CONTACTING'}
                            style={{ cursor: placeDetail?.status === 'CONTACTING' ? 'default' : 'pointer' }}
                          />
                          <Label for="isPublished" className="mb-0" style={{ cursor: placeDetail?.status === 'CONTACTING' ? 'default' : 'pointer' }}>
                            Công khai chỗ nghỉ
                          </Label>
                        </FormGroup>

                        <div className="d-flex align-items-center gap-2">
                          <Label for="placeStatus" className="mb-0 text-nowrap">
                            Trạng thái:
                          </Label>
                          <div style={{ width: '180px' }}>
                            <SelectValidation
                              id="placeStatus"
                              name="status"
                              value={placeDetail.status || ''}
                              options={PLACE_STATUS_OPTIONS}
                              onChange={onChangeStatus}
                              isHiddenSearchable
                              isDisabled={isExtranet}
                            />
                          </div>
                        </div>
                      </div>

                      <div className={styles.OtaCredentials}>
                        <div className={styles.OtaCredentialsHeader}>
                          <div className="Title mb-0">OTA Credentials</div>
                        </div>

                        <div className={styles.OtaCredentialsFields}>
                          <div>
                            <Label for="otaHotelId" className={styles.OtaCredentialsLabel}>
                              OTA Hotel Id
                            </Label>
                            <AppInputValidation
                              name="otaHotelId"
                              id="otaHotelId"
                              type="text"
                              placeholder="Nhập OTA Hotel Id"
                              validation={otaValidation}
                            />
                          </div>

                          <div>
                            <Label
                              for="otaHotelChannelKey"
                              className={styles.OtaCredentialsLabel}
                            >
                              OTA Hotel Channel Key
                            </Label>
                            <AppInputValidation
                              name="otaHotelChannelKey"
                              id="otaHotelChannelKey"
                              type="text"
                              placeholder="Nhập OTA Hotel Channel Key"
                              validation={otaValidation}
                            />
                          </div>
                        </div>

                        <div className={styles.OtaCredentialsActions}>
                          <AppButton
                            loading={isLoadingUpdatePlaceOtaCredentials}
                            onClick={otaValidation.handleSubmit}
                          >
                            Lưu
                          </AppButton>
                        </div>
                      </div>

                      <div className="InformationContentLocation">
                        <LocationYellow />

                        <div className="InformationContentLocation_Description">
                          <div className="Title">
                            {`${placeDetail.address}, ${placeDetail.province}`}
                          </div>

                          <div className="SubTitle">{renderLandmarks}</div>
                        </div>
                      </div>

                      <div className="InformationContentLocation">
                        <LocationBlue />

                        <div className="InformationContentLocation_Description">
                          <div className="Title">
                            {`${placeDetail.location?.coordinates[0]}, ${placeDetail.location?.coordinates[1]}`}
                          </div>

                          <div className="SubTitle">
                            trên
                            {' '}
                            <a
                              target="_blank"
                              href={`https://www.google.com/maps/place/${placeDetail.location?.coordinates[0]},${placeDetail.location?.coordinates[1]}`}
                              rel="noreferrer"
                            >
                              <span className="Hyperlink">Google Maps</span>
                            </a>
                          </div>
                        </div>
                      </div>

                      <hr />

                      <div className="InformationContentDescription">
                        <p className="Title">Miêu tả chỗ nghỉ</p>

                        <div
                          dangerouslySetInnerHTML={{
                            __html: placeDetail.description,
                          }}
                          className="WhiteSpaceBreakLine"
                        />

                        {isExtranet && (
                          <div className="InformationContentDescription__Header">
                            <AppButton
                              classes={styles.EditButton}
                              onClick={onToggleModalEditLocation}
                            >
                              Chỉnh sửa
                            </AppButton>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <SectionReviews placeId={placeId} reviewList={bookingReviewList} />

                </Col>

                <Col md={5}>
                  <div className="ImageWrapper">
                    <img
                      className="Image"
                      src={placeDetail.previewImage}
                      alt="avatar"
                    />
                  </div>
                </Col>
              </div>
            </div>

            {!isExtranet && (
              <div className={styles.StickyButtons}>
                <div className={styles.ButtonWrap}>
                  {/* <AppButton
                    isDefault
                    classes={styles.OutlineButton}
                    onClick={onDeleteLocation}
                  >
                    Xóa chỗ nghỉ
                  </AppButton> */}

                  <AppButton onClick={onToggleModalEditLocation}>
                    Chỉnh sửa
                  </AppButton>
                </div>
              </div>
            )}
          </div>

          {isOpenModalEditLocation && (
            <ModalEditLocation
              isOpenModalEditLocation={isOpenModalEditLocation}
              onToggleModal={onToggleModalEditLocation}
              validation={validation}
              provinces={provinces}
              districts={districts}
              wards={wards}
              isLoading={isLoadingUpdatePlace}
            />
          )}

          {/* {!isExtranet && (
            <ModalRequestAction
              isOpen={isOpenModalDeleteLocation}
              icon={images.modalGarbageCan.default}
              textRequest={'Bạn có chắc chắn muốn xóa \n chỗ nghỉ này không?'}
              onToggle={onToggleModalDeleteLocation}
              onCloseModal={onCloseModalDeleteLocation}
              onConfirm={onConfirmDeleteLocation}
            />
          )} */}
        </Container>
      </div>
    </>
  );
}

Location.propTypes = {
  isLoadingGetPlaceDetail: PropTypes.bool.isRequired,
  isLoadingUpdatePlace: PropTypes.bool.isRequired,
  isLoadingUpdatePlaceOtaCredentials: PropTypes.bool.isRequired,

  bookingReviewList: PropTypes.instanceOf(Array).isRequired,
  placeDetail: PropTypes.instanceOf(Object),
  provinces: PropTypes.instanceOf(Array).isRequired,
  districts: PropTypes.instanceOf(Array).isRequired,
  wards: PropTypes.instanceOf(Array).isRequired,

  actionGetPlaceDetail: PropTypes.func.isRequired,
  actionUpdatePlace: PropTypes.func.isRequired,
  // actionDeleteLocation: PropTypes.func.isRequired,
  actionUpdatePlaceOtaCredentials: PropTypes.func.isRequired,
  actionGetProvinceList: PropTypes.func.isRequired,
  actionGetDistrictList: PropTypes.func.isRequired,
  actionGetWardList: PropTypes.func.isRequired,
  actionGetBookingReviewList: PropTypes.func.isRequired,
};

Location.defaultProps = {
  placeDetail: INITIAL_VALUE.OBJECT,
};

export default Location;
